import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { createBudgetChangeOrder, patchBudgetChangeOrder } from '@/api/Sherpa/ChangeOrder';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { DATE_FORMAT } from '@/constants';
import { useUploadDocuments } from '@/features/ChangeOrder/hooks/useUploadDocuments';
import BudgetHookForm from '@/features/ChangeOrder/Summary/ChangeOrderBudget/BudgetForm';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { type BudgetChangeOrderSchema, validationSchema } from '../BudgetForm/validationSchema';

type AddBudgetModalProps = {
  projectId: string;
  onSubmitted?: () => void;
};

export const AddBudgetModal = NiceModal.create<AddBudgetModalProps>(({ projectId, onSubmitted }) => {
  const modal = useModal();
  const dispatch = useAppDispatch();

  const onSuccess = () => {
    onSubmitted?.();
    modal.hide();
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Budget line item change order created successfully',
        toastType: 'success',
      })
    );
  };

  const { uploadDocuments, isLoading } = useUploadDocuments({
    onUploadReady: ({ documents, params }) =>
      patchBudgetChangeOrder({
        changeOrderId: params.changeOrderId,
        documents,
      }),
    onSuccess,
    resourceType: 'changeOrderBudgetLineItem',
  });

  const createMutation = useMutation({
    mutationFn: createBudgetChangeOrder,
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const validateFiles = (templateDocuments: any[]) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    templateDocuments?.forEach((file: File) => {
      const acceptedFileTypes = getAcceptedFileTypes().split(',');
      if (acceptedFileTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
          toastType: 'failed',
        })
      );

      return null;
    }

    return validFiles;
  };

  const onSubmit = async (formData: BudgetChangeOrderSchema) => {
    const response = await createMutation.mutateAsync({
      refType: 'project',
      refId: projectId!,
      phaseSlug: formData.phase?.value ?? '',
      lineItemSlug: formData.lineItem?.value ?? '',
      reason: formData.reason,
      consequences: formData.noResponseConsequence,
      proposedAmount: formData.proposedAmount,
      responseDueBy: dayjs(formData.responseDueBy).format(DATE_FORMAT),
      notes: formData.notes,
    });

    if (formData.templateDocuments?.length) {
      const validFiles = validateFiles(formData.templateDocuments);

      if (validFiles && validFiles.length > 0) {
        uploadDocuments({
          files: formData.templateDocuments,
          params: { changeOrderId: response.id },
        });
      }
    } else {
      onSuccess();
    }
  };

  if (!modal.visible) return null;

  return (
    <Modal customStyle='max-w-xl p-0'>
      <HookFormProvider
        defaultValues={{
          phase: null,
          lineItem: null,
        }}
        resolver={yupResolver(validationSchema) as any}
      >
        <BudgetHookForm
          title='Add New Budget Line Item Change Order'
          hide={modal.hide}
          projectId={projectId}
          onSubmit={onSubmit}
          isLoading={createMutation.isLoading || isLoading}
        />
      </HookFormProvider>
    </Modal>
  );
});
